import { useRef, useState } from "react";
import { TGenericGUIDName, TProcessStates } from "../../../lib/types/general";
import { isNumberFromString, phoneFormatHandler, phoneRegex, websiteRegex } from "../../../lib/common/validation";
import { LabSignUpFormData, TApiAddress, TApiLab, TCitiesData } from "../../../lib/types/labs";
import { evaluators, useWindowSizeMatch } from "../../../lib/window-size-matcher";
import { DEVICES_SIZES_IN_PX } from "../../../lib/common/constants";
import { useAddress } from "../../../services/regions";
import { BusinessLocationMapOneMarkerStatic, BusinessLocationMapOneMarkerDraggable } from "./map";
import { usa_flag } from "../../../images/flags";
import { Spinner } from "../../../components/common/spinner";

type TAboutTheLabSectionProps = {
    editable: boolean;
    isSomePartBeingEdited: boolean;
    userInfo: TApiLab;
    desiredRegion: TGenericGUIDName[];
    workingState: TProcessStates;
    setUserInfo: React.Dispatch<React.SetStateAction<TApiLab>>;
    setIsSomePartBeingEdited: (value: React.SetStateAction<boolean>) => void;
    updateLabAndLocalStorage: (data: LabSignUpFormData, name?: string) => Promise<void>;
    userLatitude: number;
    userLongitude: number;
    coordinatesHandler: (userId: number) => Promise<void>;
    verifyDuplicateNameAddress: (name: string, address: TApiAddress, userId: number) => Promise<boolean>;
};

export const AboutTheLabSection = ({
    editable,
    isSomePartBeingEdited,
    userInfo,
    workingState,
    desiredRegion,
    setUserInfo,
    setIsSomePartBeingEdited,
    updateLabAndLocalStorage,
    userLatitude,
    userLongitude,
    coordinatesHandler,
    verifyDuplicateNameAddress,
}: TAboutTheLabSectionProps) => {
    const [isEditingAbout, setIsEditingAbout] = useState(false);
    const [citiesList, setCitiesList] = useState<TCitiesData[]>([]);
    const isSearching = useRef<boolean>(false);
    const { getCities, updateCoordinates } = useAddress();
    const [selectedCity, setSelectedCity] = useState("");
    const [selectedState, setSelectedState] = useState("");
    const [newUserLat, setNewUserLat] = useState<number>();
    const [newUserLong, setNewUserLong] = useState<number>();
    const [regionIsTyping, setRegionIsTyping] = useState<boolean>();
    const [viewNewRegion, setViewNewRegion] = useState<string>();
    const [currWorkingState, setCurrWorkingState] = useState<TProcessStates>("done");

    const isPhoneVerified = userInfo.verifications.phone === true;

    const editableOnClickHandler = (e: any) => {
        if (!editable) return;
        if (isSomePartBeingEdited) return;
        e.preventDefault();
        e.stopPropagation();
        setIsEditingAbout(true);
        setIsSomePartBeingEdited(true);
        refRegion.current = { value: userInfo.other.desiredRegion }; // refRegion has no input. We need to initialize refRegion in edit handler, since it dynamically changes derived from other inputs (city, state).
    };
    // const refLocation = useRef<any>();
    const refRegion = useRef<any>({});
    const refPhone = useRef<any>();
    const refSize = useRef<any>();
    const refSpecialty = useRef<any>();
    const refHistory = useRef<any>();
    const refZip = useRef<any>();
    const refWebSite = useRef<any>();
    const refStreet = useRef<any>();
    const refStreetNumber = useRef<any>();
    const refCity = useRef<any>();
    const mapInModalRef = useRef<HTMLDivElement>(null);

    const [errorPhone, setErrorPhone] = useState("");
    const [errorZip, setErrorZip] = useState("");
    const [errorWebsite, setErrorWebsite] = useState("");
    const [errorStreet, setErrorStreet] = useState("");
    const [errorCity, setErrorCity] = useState(false);
    const [generalError, setGeneralError] = useState(false);
    const [duplicateAddress, setDuplicateAddress] = useState(false);

    const saveData = async (e: any) => {
        e.preventDefault();
        e.stopPropagation();
        setCurrWorkingState("working");
        setDuplicateAddress(false);
        setGeneralError(false);

        const phoneValid = refPhone.current.value;
        const zipValid = refZip.current.value;
        const websiteValid = refWebSite.current.value;
        const streetValid = refStreet.current.value.trim();
        const streetNumberValid = refStreetNumber.current.value.trim();
        const cityInput = refCity.current.value.trim();
        const [cityCurrentValue, stateAddressCurrentValue] = cityInput.split(",").map((s: string) => s?.trim() || "");

        const cityToSave = selectedCity || cityCurrentValue;
        const stateToSave = selectedState || stateAddressCurrentValue;
        const zipIsInvalid = !isNumberFromString(zipValid);

        let hasError = false;

        if (zipIsInvalid) {
            setErrorZip("Please use numbers only for your ZIP.");
            hasError = true;
        } else {
            setErrorZip("");
        }
        if (!phoneRegex.test(phoneValid)) {
            setErrorPhone("Please enter a valid 10-digit US phone number.");
            hasError = true;
        } else {
            setErrorPhone("");
        }
        if (websiteValid && !websiteRegex.test(websiteValid)) {
            setErrorWebsite("Please check that your URL is correct.");
            hasError = true;
        } else {
            setErrorWebsite("");
        }
        if (!streetValid || streetValid.trim().length === 0) {
            setErrorStreet("Street Address field is required.");
            hasError = true;
        } else {
            setErrorStreet("");
        }
        if (!cityToSave || !stateToSave || cityInput.trim().length === 0) {
            setErrorCity(true);
            hasError = true;
        } else {
            setErrorCity(false);
        }
        if (hasError) {
            setGeneralError(true);
            setCurrWorkingState("done");
            return;
        }

        const newData = {
            ...userInfo.other,
            // location: refLocation.current.value,
            phone: refPhone.current.value,
            website: refWebSite.current.value,
            size: refSize.current.value,
            specialty: refSpecialty.current.value,
            history: refHistory.current.value,
            zip: refZip.current.value,
            desiredRegion: refRegion.current.value,
            address: {
                street: streetValid,
                street_number: streetNumberValid,
                city: cityToSave,
                state: stateToSave,
            },
        };

        const validateDuplicate = await verifyDuplicateNameAddress(userInfo.name, newData.address, userInfo.id);

        if (validateDuplicate) {
            setDuplicateAddress(true);
            hasError = true;
            setCurrWorkingState("done");
            return;
        }

        await updateLabAndLocalStorage(
            {
                password: "",
                ...newData,
            },
            "",
        );

        if (userInfo.other.zip !== newData.zip && userInfo.other.aboutRestaurants) {
            newData.aboutRestaurants = [];
        }
        if (userInfo.other.zip !== newData.zip && userInfo.other.aboutEvents) {
            newData.aboutEvents = [];
        }
        if (userInfo.other.zip !== newData.zip && userInfo.other.aboutLandmarks) {
            newData.aboutLandmarks = [];
        }

        const oldPhone = userInfo?.other?.phone;
        const newPhone = newData.phone;
        let isPhoneVerified = userInfo?.verifications?.phone;

        if (isPhoneVerified && oldPhone !== newPhone) isPhoneVerified = false;

        setUserInfo({
            ...userInfo,
            other: newData,
            verifications: {
                ...userInfo.verifications,
                phone: isPhoneVerified,
            },
        });

        setIsEditingAbout(false);
        setIsSomePartBeingEdited(false);
        setDuplicateAddress(false);
        setGeneralError(false);
        setCurrWorkingState("done");
    };

    const regionList = (): TGenericGUIDName | undefined => {
        const newRegion = viewNewRegion || userInfo.other?.desiredRegion;

        if (!newRegion) return undefined;

        const selectedRegion = desiredRegion.find((region) => {
            return newRegion.includes(region.guid);
        });
        return selectedRegion;
    };

    const cancelHandler = async (e: any) => {
        e.stopPropagation();
        e.preventDefault();
        setDuplicateAddress(false);
        setIsEditingAbout(false);
        setIsSomePartBeingEdited(false);
    };

    const { conditionFulfilled: isSmallDevice } = useWindowSizeMatch({
        condition: evaluators.lessThan(DEVICES_SIZES_IN_PX.small),
    });

    const searchCityByName = async () => {
        if (!refCity.current.value) {
            setCitiesList([]);
            return;
        }
        if (isSearching.current) return;

        isSearching.current = true;
        const cityToSearch = refCity.current.value.toLocaleLowerCase().trim();
        const results = await getCities(cityToSearch);

        isSearching.current = false;
        const newCityToSearch = refCity.current.value.toLocaleLowerCase().trim();
        if (cityToSearch !== newCityToSearch) {
            searchCityByName();
            return;
        }
        setCitiesList(
            results.data.map(
                (city): TCitiesData => ({
                    city_id: city.city_id,
                    city_name: city.city_name,
                    county: city.county,
                    state_id: city.state_id,
                    state_name: city.state_name,
                    state_abb: city.state_abb,
                    region_id: city.region_id,
                }),
            ),
        );
    };

    const handleNewCoordinates = (lat: number, lng: number) => {
        setNewUserLat(lat);
        setNewUserLong(lng);
    };

    const saveNewCoordinates = async () => {
        if (!newUserLat || !newUserLong) return;
        setCurrWorkingState("working");
        await updateCoordinates(newUserLat, newUserLong, userInfo.id);
        await coordinatesHandler(userInfo.id);
        setIsSomePartBeingEdited(false);
        setCurrWorkingState("done");
    };

    return (
        <div
            onClick={() => {
                if (isSomePartBeingEdited) return;
            }}
        >
            <div className="d-flex justify-content-end">
                {editable && !isSomePartBeingEdited && (
                    <div
                        className="fileTooltip rightEdit"
                        data-tooltip=" Edit "
                        role="button"
                        onClick={(e) => {
                            editableOnClickHandler(e);
                        }}
                    >
                        <i data-testid="edit-icon" className="fa-solid fa-pen-to-square fa-xs p-2 text-center shadow editIcon"></i>
                    </div>
                )}
            </div>
            {!isEditingAbout && (
                <div className="text-break ">
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Region: </strong>
                        <small className={" fw-bold ms-2"}>{regionList()?.name}</small>
                    </p>
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Address: </strong> <br />
                        <small>
                            {userInfo.other.address?.street}
                            {userInfo.other.address?.street_number ? `, ${userInfo.other.address.street_number}` : ""}
                            {userInfo.other.address?.street && <br />}
                            {userInfo.other.address?.city}, {userInfo.other.address?.state} {userInfo.other.zip}
                            <br />
                            United States
                        </small>
                        {/* {userInfo.other.location} */}
                    </p>
                    {userInfo.other.address?.street && userInfo.other.address?.city && userInfo.other.address?.state && (
                        <div className="position-relative">
                            <div className="mt-2 mb-3">
                                <BusinessLocationMapOneMarkerStatic lat={userLatitude} lng={userLongitude} />
                            </div>
                            {editable && (
                                <>
                                    <div
                                        className="position-absolute"
                                        style={{
                                            right: 10,
                                            top: 10,
                                            zIndex: 998,
                                        }}
                                    >
                                        <div
                                            className="fileTooltip rightEdit"
                                            data-tooltip=" Edit Map "
                                            role="button"
                                            data-bs-toggle="modal"
                                            data-bs-target="#editUserMapModal"
                                            onClick={() => {
                                                setIsSomePartBeingEdited(true);
                                            }}
                                        >
                                            <i data-testid="edit-icon" className="fa-solid fa-map-location-dot fa-xs p-2 text-center shadow editIcon"></i>
                                        </div>
                                    </div>

                                    <div
                                        className="modal fade"
                                        id="editUserMapModal"
                                        data-backdrop="static"
                                        data-keyboard="false"
                                        tabIndex={-1}
                                        aria-labelledby="editUserMapModalLabel"
                                        aria-hidden="true"
                                        ref={mapInModalRef}
                                    >
                                        <div className="modal-dialog modal-dialog-centered">
                                            <div className="modal-content">
                                                <div className="modal-header">
                                                    <h1 className="modal-title fs-5" id="editUserMapModalLabel">
                                                        Adjust your map location{" "}
                                                    </h1>
                                                    <button
                                                        type="button"
                                                        className="btn-close"
                                                        data-bs-dismiss="modal"
                                                        aria-label="Close"
                                                        onClick={() => {
                                                            setIsSomePartBeingEdited(false);
                                                        }}
                                                    ></button>
                                                </div>
                                                <div className="modal-body">
                                                    <p className="text-muted mb-3">
                                                        <small>
                                                            Drag the pin to your exact location if you need to make an adjustment. This helps others see your
                                                            precise spot on the map.
                                                        </small>
                                                    </p>

                                                    <BusinessLocationMapOneMarkerDraggable
                                                        lat={userLatitude}
                                                        lng={userLongitude}
                                                        onChangeCoordinates={handleNewCoordinates}
                                                        mapInModalRef={mapInModalRef}
                                                    />
                                                </div>
                                                <div className="modal-footer">
                                                    <button
                                                        type="button"
                                                        className="btn btn-primary"
                                                        data-bs-dismiss="modal"
                                                        onClick={() => {
                                                            saveNewCoordinates();
                                                        }}
                                                    >
                                                        Save
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="btn btn-danger"
                                                        data-bs-dismiss="modal"
                                                        onClick={() => {
                                                            setIsSomePartBeingEdited(false);
                                                        }}
                                                    >
                                                        Cancel
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    )}
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Phone Number: </strong>
                        {!!userInfo.other.phone ? (
                            <>
                                {phoneFormatHandler(userInfo.other.phone)}{" "}
                                {isPhoneVerified && <i title="Phone Verified" className="fas fa-check-circle text-success"></i>}
                            </>
                        ) : (
                            ""
                        )}
                    </p>

                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Website: </strong>
                        <a
                            href={`${userInfo.other.website?.startsWith("http") ? userInfo.other.website : `http://${userInfo.other.website}`}`}
                            target="_blank"
                            className="btn-link"
                            rel="noopener noreferrer"
                        >
                            {userInfo.other.website}
                        </a>
                    </p>
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Size: </strong> {userInfo.other.size}
                    </p>
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Specialty: </strong> {userInfo.other.specialty}
                    </p>
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">History: </strong> {userInfo.other.history}
                    </p>
                </div>
            )}
            {isEditingAbout && (
                <div className="text-break ">
                    <p className={workingState !== "done" || currWorkingState !== "done" ? "cssLoading" : ""}>
                        <strong className="text-primary">Region: </strong>
                        <small className={(regionIsTyping ? "cssLoading text-muted" : "fw-bold") + " px-4 py-2 bg-light border rounded  ms-2"}>
                            {regionList()?.name}
                        </small>
                    </p>

                    <p>
                        <strong className="text-primary">Address: {""}</strong>
                        <small className="text-primary fst-italic">(required)</small>
                    </p>
                    <div className="row mb-2">
                        <div className="col-md-6">
                            <div className="mb-2 position-relative">
                                <input
                                    ref={refStreet}
                                    type="text"
                                    className={`form-control ${errorStreet ? "border-danger" : ""}`}
                                    defaultValue={userInfo.other.address?.street}
                                    placeholder="Street Address *"
                                    maxLength={60}
                                />
                                <i className={`fa-solid fa-xs errorIconInputs ${errorStreet ? "fa-triangle-exclamation text-danger" : ""}`}></i>
                            </div>
                            {isSmallDevice.fulfilled && (
                                <div className="mb-2 position-relative">
                                    <input
                                        ref={refStreetNumber}
                                        type="text"
                                        className="form-control"
                                        defaultValue={userInfo.other.address?.street_number}
                                        placeholder="Suite Number"
                                        maxLength={10}
                                    />
                                </div>
                            )}
                            <div className="mb-2 position-relative">
                                <input
                                    className={`form-control ${errorCity ? "border-danger" : ""}`}
                                    ref={refCity}
                                    placeholder="Find your city *"
                                    type="text"
                                    maxLength={50}
                                    onChange={() => {
                                        setSelectedCity("");
                                        setSelectedState("");
                                        searchCityByName();
                                        setRegionIsTyping(true);
                                    }}
                                    defaultValue={`${userInfo.other.address?.city}, ${userInfo.other.address?.state}`}
                                />
                                <i
                                    className={`fa-solid fa-xs errorIconInputs ${
                                        errorCity ? "fa-triangle-exclamation text-danger" : "fa-magnifying-glass text-muted"
                                    }`}
                                ></i>

                                {citiesList.length > 0 && (
                                    <>
                                        <ul className="w-100 mt-2 list-group searchResultStyle rounded shadow">
                                            {citiesList.map((v) => (
                                                <li
                                                    className="list-group-item list-group-item-action py-2 px-3"
                                                    onClick={() => {
                                                        setSelectedCity(v.city_name);
                                                        setSelectedState(v.state_abb);
                                                        refCity.current.value = `${v.city_name}, ${v.state_abb}`;
                                                        refRegion.current.value = v.region_id;
                                                        setViewNewRegion(v.region_id);
                                                        setCitiesList([]);
                                                        setRegionIsTyping(false);
                                                    }}
                                                >
                                                    {" "}
                                                    {v.city_name}, {v.state_abb}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </div>
                            {!isSmallDevice.fulfilled && <p className="ps-2 pt-2">United States</p>}
                        </div>
                        <div className="col-md-6">
                            {!isSmallDevice.fulfilled && (
                                <div className="mb-2 position-relative">
                                    <input
                                        ref={refStreetNumber}
                                        type="text"
                                        className="form-control"
                                        defaultValue={userInfo.other.address?.street_number}
                                        placeholder="Suite Number"
                                        maxLength={10}
                                    />
                                </div>
                            )}
                            <div className="mb-2 position-relative">
                                <input
                                    ref={refZip}
                                    defaultValue={userInfo.other.zip}
                                    type="text"
                                    className={`form-control ${errorZip ? "border-danger" : ""}`}
                                    maxLength={10}
                                    placeholder="Zip Code *"
                                />
                                <i className={`fa-solid fa-xs errorIconInputs ${errorZip ? "fa-triangle-exclamation text-danger" : ""}`}></i>
                                {errorZip && (
                                    <p className="text-danger ps-2">
                                        <small>{errorZip}</small>
                                    </p>
                                )}
                            </div>
                            {isSmallDevice.fulfilled && <p className="ps-2 pt-2">United States</p>}
                        </div>
                    </div>
                    <p>
                        <strong className="text-primary">Phone Number: </strong> <span className="form-text ms-2">Example: `5551234567`</span>
                        <div className="input-group">
                            <div className="input-group-text bg-light">
                                <img src={usa_flag} alt="usa_flag" className="me-2" width={20} />
                                <span className="d-none d-md-block">United States</span>
                                <span className="text-muted ms-md-2">+1</span>
                            </div>
                            <input
                                ref={refPhone}
                                defaultValue={userInfo.other.phone}
                                type="tel"
                                maxLength={10}
                                placeholder="5551234567"
                                className={`form-control ${errorPhone ? "border-danger" : ""}`}
                                inputMode="numeric"
                                autoComplete="tel-national"
                                onChange={() => {
                                    refPhone.current.value = refPhone.current.value.replaceAll(/\D/g, "").slice(0, 10);
                                }}
                            />
                        </div>
                        {errorPhone && (
                            <p className="text-danger ps-2">
                                <small>{errorPhone}</small>
                            </p>
                        )}
                    </p>
                    <p>
                        <strong className="text-primary">Website: </strong>{" "}
                        <input ref={refWebSite} defaultValue={userInfo.other.website} type="text" className="form-control" maxLength={50} />
                        {errorWebsite && (
                            <p className="text-danger ps-2">
                                <small>{errorWebsite}</small>
                            </p>
                        )}
                    </p>
                    <p>
                        <strong className="text-primary">Size: </strong>{" "}
                        <input ref={refSize} defaultValue={userInfo.other.size} type="text" className="form-control" maxLength={50} />
                    </p>
                    <p>
                        <strong className="text-primary">Specialty: </strong>{" "}
                        <textarea ref={refSpecialty} defaultValue={userInfo.other.specialty} className="form-control" maxLength={250} rows={5} />
                    </p>
                    <p>
                        <strong className="text-primary">History: </strong>{" "}
                        <textarea ref={refHistory} defaultValue={userInfo.other.history} className="form-control" maxLength={500} rows={8} />
                    </p>
                    <div className="d-grid gap-2 mt-2 w-100 d-md-block">
                        {generalError && (
                            <div className="alert alert-danger text-start py-1 px-2 rounded-3" role="alert">
                                <p style={{ fontSize: "small" }} className="m-0">
                                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                    Please check the highlighted fields and try again.
                                </p>
                            </div>
                        )}
                        {duplicateAddress && (
                            <div className="alert alert-danger text-start py-1 px-2 rounded-3" role="alert">
                                <p style={{ fontSize: "small" }} className="m-0">
                                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                                    Looks like this address is already assigned to another business. Please verify your address.
                                </p>
                            </div>
                        )}

                        <button className="btn btn-primary px-4" onClick={saveData} disabled={currWorkingState === "working"}>
                            {currWorkingState === "working" ? <Spinner /> : <>Save</>}
                        </button>

                        <button
                            className="btn btn-danger ms-lg-2 px-3 mt-md-2 mt-lg-0"
                            onClick={(e) => {
                                cancelHandler(e);
                            }}
                            disabled={currWorkingState === "working"}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};
